import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createDefaultPushPreferenceValues,
  subscribeToPushPreferences,
  updatePushPreferenceCategory,
  type PushPreferenceCategory,
  type PushPreferenceValues,
  type PushPreferencesReadResult,
} from '../services/pushPreferencesService';

export type PushPreferencesStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface PushPreferencesController {
  uid: string | null;
  status: PushPreferencesStatus;
  values: PushPreferenceValues;
  source: 'missing' | 'persisted' | null;
  error: unknown | null;
  pendingCategories: ReadonlySet<PushPreferenceCategory>;
  writeError: { category: PushPreferenceCategory; error: unknown } | null;
  updateCategory: (category: PushPreferenceCategory, enabled: boolean) => Promise<void>;
}

export const usePushPreferences = (): PushPreferencesController => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const activeUidRef = useRef<string | null>(uid);
  const uidGenerationRef = useRef(0);
  if (activeUidRef.current !== uid) {
    activeUidRef.current = uid;
    uidGenerationRef.current += 1;
  }
  const [status, setStatus] = useState<PushPreferencesStatus>(uid ? 'loading' : 'idle');
  const [stateOwnerUid, setStateOwnerUid] = useState<string | null>(uid);
  const [values, setValues] = useState<PushPreferenceValues>(() =>
    createDefaultPushPreferenceValues()
  );
  const [source, setSource] = useState<'missing' | 'persisted' | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [pendingCategories, setPendingCategories] = useState<Set<PushPreferenceCategory>>(
    () => new Set()
  );
  const [writeError, setWriteError] = useState<{
    category: PushPreferenceCategory;
    error: unknown;
  } | null>(null);

  useEffect(() => {
    let active = true;
    const subscriptionUid = uid;
    const subscriptionGeneration = uidGenerationRef.current;
    const isCurrentSubscription = () =>
      active &&
      activeUidRef.current === subscriptionUid &&
      uidGenerationRef.current === subscriptionGeneration;
    setStateOwnerUid(uid);
    setPendingCategories(new Set());
    setWriteError(null);
    setValues(createDefaultPushPreferenceValues());
    setSource(null);
    setError(null);
    setStatus(uid ? 'loading' : 'idle');

    if (!uid) {
      return () => {
        active = false;
      };
    }

    const applyResult = (result: PushPreferencesReadResult) => {
      if (!isCurrentSubscription()) return;
      if (result.kind === 'missing') {
        setValues(result.push);
        setSource('missing');
        setError(null);
        setStatus('ready');
        return;
      }
      if (result.kind === 'present') {
        setValues(result.push);
        setSource('persisted');
        setError(null);
        setStatus('ready');
        return;
      }
      setError(result.error);
      setStatus('unavailable');
    };

    const unsubscribe = subscribeToPushPreferences(uid, applyResult, (subscriptionError) => {
      if (!isCurrentSubscription()) return;
      setError(subscriptionError);
      setStatus('unavailable');
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [uid]);

  const updateCategory = useCallback(
    async (category: PushPreferenceCategory, enabled: boolean): Promise<void> => {
      if (!uid) {
        throw new Error('push-preferences-auth-required');
      }
      const operationUid = uid;
      const operationGeneration = uidGenerationRef.current;
      const isCurrentOperation = () =>
        activeUidRef.current === operationUid && uidGenerationRef.current === operationGeneration;
      if (!isCurrentOperation()) return;
      setWriteError(null);
      setPendingCategories((current) => {
        const next = new Set(current);
        next.add(category);
        return next;
      });
      try {
        const confirmed = await updatePushPreferenceCategory(category, enabled);
        if (isCurrentOperation()) {
          setValues(confirmed.push);
          setSource('persisted');
          setError(null);
          setStatus('ready');
        }
      } catch (updateError) {
        if (isCurrentOperation()) {
          setWriteError({ category, error: updateError });
        }
        throw updateError;
      } finally {
        if (isCurrentOperation()) {
          setPendingCategories((current) => {
            const next = new Set(current);
            next.delete(category);
            return next;
          });
        }
      }
    },
    [uid]
  );

  const ownsCurrentState = stateOwnerUid === uid;
  const visibleValues = ownsCurrentState ? values : createDefaultPushPreferenceValues();
  const visibleStatus = ownsCurrentState ? status : uid ? 'loading' : 'idle';
  const visibleSource = ownsCurrentState ? source : null;
  const visibleError = ownsCurrentState ? error : null;
  const emptyPendingCategories = useMemo<ReadonlySet<PushPreferenceCategory>>(
    () => new Set<PushPreferenceCategory>(),
    []
  );
  const visiblePendingCategories = ownsCurrentState ? pendingCategories : emptyPendingCategories;
  const visibleWriteError = ownsCurrentState ? writeError : null;

  return useMemo(
    () => ({
      uid,
      status: visibleStatus,
      values: visibleValues,
      source: visibleSource,
      error: visibleError,
      pendingCategories: visiblePendingCategories,
      writeError: visibleWriteError,
      updateCategory,
    }),
    [
      uid,
      visibleStatus,
      visibleValues,
      visibleSource,
      visibleError,
      visiblePendingCategories,
      visibleWriteError,
      updateCategory,
    ]
  );
};
